// DangerConfirmModal — premium destructive-action gate.
//
// Wraps the shared <Modal> shell (so spring-scale entrance + bottom-sheet on
// mobile + ESC + body-overflow-lock all stay consistent with the rest of the
// app), and adds:
//
//   • Rose accent border + glowing red drop-shadow so the admin feels the
//     gravity of what they're about to do (per the Phase 19 / Batch 2
//     "premium danger aesthetic" direction).
//   • Optional `confirmWord` — when set, an input appears and the
//     final submit button stays disabled until the admin types the
//     literal confirm word (case-sensitive). Used for nuclear ops like
//     "Disable ALL bots" where a sleepy misclick is unacceptable.
//   • Optional `reasonLabel` / `reasonPlaceholder` / `reasonRequired` —
//     when set, a textarea appears so admins can capture WHY they're
//     taking the action. The reason is passed through to onConfirm so
//     it can land in the audit log.
//   • Async-aware confirm — onConfirm may return a Promise; the submit
//     button shows a busy state until it resolves and surfaces any
//     thrown error inline.
//
// Used by:
//   - BotMatrixPanel "Disable ALL" / "Disable category" (typed confirm)
//   - AdminUserDetail "Suspend" / "Ban" (reason capture)
//   - DlqPanel per-row "Retry" (simple confirm)
//
// NOT a portal-of-portals: the underlying Modal already portals into
// document.body, so we don't double-portal.

import { useEffect, useState, ReactNode } from 'react';
import { Modal } from './Modal';
import Icon from './Icon';

export type DangerConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Called when the admin confirms. May return a Promise; while pending
   * the modal shows a busy state. If the promise rejects, the error
   * message is displayed inline and the modal stays open.
   *
   * Receives the optional reason text the admin typed (or `undefined`
   * if the reasonLabel prop was not set).
   */
  onConfirm: (reason?: string) => void | Promise<void>;

  /** Title — short imperative, e.g. "Disable ALL bots?" */
  title: string;
  /** Body — single sentence or short paragraph explaining the impact. */
  description: ReactNode;

  /** Submit button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;

  /**
   * If set, the admin must type this word verbatim into a confirmation
   * input before the submit button enables. Case-sensitive. Used for
   * the most destructive ops to defeat sleepy misclicks.
   */
  confirmWord?: string;

  /**
   * If set, a reason textarea is shown. The reason is passed to
   * onConfirm and is intended to be forwarded to the backend audit log.
   */
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
};

export default function DangerConfirmModal({
  open, onClose, onConfirm,
  title, description,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  confirmWord, reasonLabel, reasonPlaceholder, reasonRequired,
}: DangerConfirmModalProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset state every time the modal closes/opens, so a previous typed
  // confirm word doesn't leak into a fresh open.
  useEffect(() => {
    if (!open) { setTyped(''); setReason(''); setErr(null); setBusy(false); }
  }, [open]);

  const wordOk = !confirmWord || typed === confirmWord;
  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const canSubmit = wordOk && reasonOk && !busy;

  async function handleConfirm() {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      await onConfirm(reasonLabel ? reason.trim() : undefined);
      // Caller is expected to call onClose itself on success (so it can
      // also push a toast / refetch). We don't auto-close here to avoid
      // racing with caller logic.
    } catch (e: any) {
      setErr(e?.message || 'Action failed');
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={() => !busy && onClose()}>
      {/* Rose accent border + glowing shadow on the inner card. The outer
          Modal already provides the .card class; we wrap our own bordered
          container inside it for the danger styling without breaking the
          Modal's own padding/animation. */}
      <div className="rounded-xl border-2 border-rose-500/70 dark:border-rose-400/60
                      shadow-[0_0_24px_rgba(244,63,94,0.35)] dark:shadow-[0_0_28px_rgba(244,63,94,0.45)]
                      bg-white dark:bg-neutral-950 -m-4 sm:-m-6 p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-3">
          <span className="grid place-items-center w-10 h-10 rounded-full
                           bg-rose-100 text-rose-600
                           dark:bg-rose-500/15 dark:text-rose-300 shrink-0">
            <Icon name="warning" size={22} filled />
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-neutral-900 dark:text-white leading-tight">
              {title}
            </h3>
            <p className="mt-1 text-[13px] text-neutral-600 dark:text-neutral-300 leading-snug">
              {description}
            </p>
          </div>
        </div>

        {/* Reason capture — optional but should appear ABOVE the typed
            confirm so admins write the why first, then the verbatim word. */}
        {reasonLabel && (
          <label className="block mt-4 text-[12px]">
            <div className="text-neutral-500 dark:text-neutral-400 mb-1 font-semibold">
              {reasonLabel}{reasonRequired && <span className="text-rose-500"> *</span>}
            </div>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm
                         bg-neutral-50 dark:bg-neutral-900
                         border border-neutral-300 dark:border-neutral-700
                         focus:outline-none focus:border-rose-400 dark:focus:border-rose-400/60
                         focus:ring-1 focus:ring-rose-400/40
                         text-neutral-900 dark:text-white"
            />
          </label>
        )}

        {/* Typed-confirm word — final gate. */}
        {confirmWord && (
          <label className="block mt-4 text-[12px]">
            <div className="text-neutral-500 dark:text-neutral-400 mb-1 font-semibold">
              Type <span className="font-mono font-bold text-rose-600 dark:text-rose-300">{confirmWord}</span> to enable the {confirmLabel.toLowerCase()} button:
            </div>
            <input
              type="text"
              autoComplete="off"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={confirmWord}
              className={`w-full px-3 py-2 rounded-lg text-sm font-mono
                          bg-neutral-50 dark:bg-neutral-900
                          border focus:outline-none focus:ring-1
                          ${wordOk
                            ? 'border-emerald-400 focus:border-emerald-400 focus:ring-emerald-400/40 text-emerald-700 dark:text-emerald-300'
                            : 'border-neutral-300 dark:border-neutral-700 focus:border-rose-400 dark:focus:border-rose-400/60 focus:ring-rose-400/40 text-neutral-900 dark:text-white'}
                        `}
            />
          </label>
        )}

        {err && (
          <div className="mt-4 px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/20
                          border border-rose-200 dark:border-rose-800/60
                          text-rose-700 dark:text-rose-300 text-[12px]">
            {err}
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl px-4 py-2.5 text-sm font-semibold
                       border border-neutral-300 dark:border-neutral-700
                       text-neutral-700 dark:text-neutral-200
                       hover:bg-neutral-50 dark:hover:bg-neutral-900
                       disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="rounded-xl px-4 py-2.5 text-sm font-bold
                       bg-rose-600 text-white hover:bg-rose-700
                       shadow-[0_0_15px_rgba(244,63,94,0.4)]
                       disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed"
          >
            {busy ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
